import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Camera, Smartphone, Sparkles, PieChart } from "lucide-react";
import SEO from "../components/SEO";

export default function ExpenseTracker() {
    return (
        <div className="min-h-screen bg-base text-primary">
            <SEO
                title="Best Expense Tracker & Management Software | AI Receipt Scanner"
                description="blinqbooks is the best expense tracker for business in 2026. AI-powered receipt scanning, automatic categorization, real-time spending insights & tax-ready reports. Track every expense effortlessly."
            />

            {/* Hero Section */}
            <section className="py-20 px-4 max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-bold uppercase mb-6">
                            <Sparkles className="w-4 h-4" />
                            Smart Expense Tracking
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold mb-6 text-primary dark:text-white leading-tight">
                            Snap, Scan, <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-500">Save Instantly</span>
                        </h1>
                        <p className="text-lg text-secondary dark:text-gray-300 mb-8 max-w-lg leading-relaxed">
                            Stop chasing paper trails. Let our AI extract data from receipts instantly. Track every penny and save thousands in overhead.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <button className="btn-primary w-full sm:w-auto px-8 py-4 shadow-lg text-lg">
                                Try for Free
                            </button>
                            <button className="btn-secondary w-full sm:w-auto px-8 py-4 shadow-sm text-lg">
                                How it Works
                            </button>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                        className="relative"
                    >
                        <div className="absolute inset-0 bg-emerald-500/10 blur-[100px] rounded-full"></div>
                        <div className="relative bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl rounded-2xl p-8">
                            <div className="flex items-center gap-4 mb-8">
                                <div className="p-4 bg-emerald-500/10 dark:bg-emerald-500/20 rounded-full">
                                    <PieChart className="w-8 h-8 text-emerald-600" />
                                </div>
                                <div>
                                    <h4 className="font-bold dark:text-white text-lg">Monthly Spending</h4>
                                    <p className="text-sm text-secondary dark:text-gray-400">Target: ₹4,000 / Actual: ₹3,210</p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                {[
                                    { name: "Coffee & Food", amount: "₹120.40", width: "45%", color: "bg-orange-500" },
                                    { name: "SaaS Tools", amount: "₹840.00", width: "75%", color: "bg-blue-500" },
                                    { name: "Office Rent", amount: "₹2,200.00", width: "90%", color: "bg-emerald-500" },
                                ].map((item, i) => (
                                    <div key={i}>
                                        <div className="flex justify-between text-sm mb-1 dark:text-gray-200">
                                            <span>{item.name}</span>
                                            <span className="font-bold">{item.amount}</span>
                                        </div>
                                        <div className="h-2 w-full bg-slate-500/10 dark:bg-white/5 rounded-full overflow-hidden">
                                            <motion.div
                                                initial={{ width: 0 }}
                                                animate={{ width: item.width }}
                                                transition={{ duration: 1, delay: i * 0.2 }}
                                                className={`h-full ${item.color}`}
                                            ></motion.div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Features Info */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
                        {[
                            { title: "AI Scanning", desc: "OCR tech extracts data from receipts with 99.9% accuracy.", icon: <Camera className="w-10 h-10 mx-auto text-emerald-500 mb-4" /> },
                            { title: "Mobile App", desc: "Scan on the go. Available for iOS and Android.", icon: <Smartphone className="w-10 h-10 mx-auto text-blue-500 mb-4" /> },
                            { title: "Tax Compliance", desc: "Automatically categorize expenses for tax filings.", icon: <CheckCircle2 className="w-10 h-10 mx-auto text-purple-500 mb-4" /> },
                            { title: "Insights", desc: "Real-time pie charts shows where your money goes.", icon: <PieChart className="w-10 h-10 mx-auto text-orange-500 mb-4" /> },
                        ].map((f, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                            >
                                {f.icon}
                                <h3 className="text-xl font-bold mb-2 dark:text-white">{f.title}</h3>
                                <p className="text-secondary dark:text-gray-400 text-sm">{f.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-20 px-4">
                <div className="max-w-4xl mx-auto bg-emerald-600 dark:bg-accent rounded-3xl p-12 text-center text-white relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-[80px] -ml-32 -mt-32"></div>
                    <h2 className="text-3xl md:text-5xl font-bold mb-6 relative z-10">Stop Manual Entry</h2>
                    <p className="text-emerald-50 mb-8 text-lg relative z-10">Automate your expenses today with blinqbooks.</p>
                    <button className="px-10 py-4 bg-white text-emerald-600 dark:text-accent rounded-xl font-bold hover:bg-opacity-90 transition-all flex items-center gap-2 mx-auto relative z-10 shadow-xl">
                        Get Started for Free <ArrowRight className="w-5 h-5" />
                    </button>
                </div>
            </section>
        </div>
    );
}
